import { useTenantData } from '@dev-agents/tenant-context';
import { type ReactNode, useEffect } from 'react';
import { useQueryLoader } from 'react-relay';

import type { userPermContextProviderQuery } from '../../../controllers/__generated__/userPermContextProviderQuery.graphql';
import Query from '../../../controllers/__generated__/userPermContextProviderQuery.graphql';
import { fg } from '../../../controllers/feature-gates';
import { useJiraAdminPermissions } from '../../../controllers/use-jira-admin-permissions';
import { useSitePermissions } from '../../../controllers/use-site-permissions';
import { UserPermContextProvider } from '../../../controllers/user-perm-context-provider';
import { useAuthData } from '../../../utils/auth-context';

import { LazyAppRouter } from './lazy-app-router';

export const LazyAppRouterWithUsersPermsContext = ({ children }: { children: ReactNode }) => {
	const [userPermsQueryRef, loadUserPermsQuery] =
		useQueryLoader<userPermContextProviderQuery>(Query);
	const { tenantContexts } = useTenantData();
	const { user } = useAuthData();
	const cloudID = tenantContexts[0]?.cloudId || '';
	const atlassianAccountId = user.accountId;
	const {
		getJiraAdminPermissions,
		permissions,
		loading: isJiraAdminPermissionLoading,
	} = useJiraAdminPermissions();
	const {
		getSitePermissions,
		permissions: sitePermissions,
		loading: isSitePermissionLoading,
	} = useSitePermissions();

	useEffect(() => {
		loadUserPermsQuery({
			cloudID,
			atlassianAccountId,
		});
		if (cloudID && fg('rovo-dev-agents-permissions-patch-enabled')) {
			getJiraAdminPermissions(cloudID);
		}
		if (cloudID && fg('rovo-dev-agents-site-permissions-patch-enabled')) {
			getSitePermissions(cloudID);
		}
	}, [
		loadUserPermsQuery,
		cloudID,
		atlassianAccountId,
		getJiraAdminPermissions,
		getSitePermissions,
	]);

	return (
		userPermsQueryRef && (
			<UserPermContextProvider
				queryRef={userPermsQueryRef}
				jiraAdminPermissions={!isJiraAdminPermissionLoading ? permissions : []}
				sitePermissions={!isSitePermissionLoading ? sitePermissions : []}
			>
				<LazyAppRouter>{children}</LazyAppRouter>
			</UserPermContextProvider>
		)
	);
};
